import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FormEvent, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { changeEmail } from "api/SignInMethods";
import { FOCUS_RING, TOUCH_TARGET } from "utils/chackra/controlStyles";
import { changeEmailOutcome, emailAddressErrorId } from "./linkErrors";

interface EmailLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  onLinked: () => void;
  /** The dialog has fully gone, exit animation included. */
  onClosed?: () => void;
}

/**
 * Ask for an address and send the confirmation letter. The email is attached
 * only later, from the letter's link. The success text is deliberately the same
 * whether or not the address is free: this form tells no one who has an account.
 */
const EmailLinkModal = ({ isOpen, onClose, userId, onLinked, onClosed }: EmailLinkModalProps) => {
  const intl = useIntl();
  const toast = useToast();
  const [address, setAddress] = useState("");
  const [errorId, setErrorId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const sendInFlight = useRef(false);
  // Told only once the dialog is gone: the card's refresh then moves focus,
  // which an open dialog's focus lock would pull straight back.
  const linkedOnClose = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const close = () => {
    setAddress("");
    setErrorId(null);
    onClose();
  };

  const report = (id: string, status: "info" | "error") =>
    toast({
      title: intl.formatMessage({ id }),
      status,
      position: "top",
      duration: 6000,
      isClosable: true,
    });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (sendInFlight.current) return;
    const invalid = emailAddressErrorId(address);
    setErrorId(invalid);
    if (invalid) return;

    sendInFlight.current = true;
    setIsSending(true);
    try {
      await changeEmail(address.trim(), userId);
      report("signInMethods.email.sent", "info");
      close();
    } catch (error) {
      const outcome = changeEmailOutcome(error);
      if (outcome.inline) {
        setErrorId(outcome.messageId);
      } else {
        report(outcome.messageId, "error");
      }
      if (outcome.alreadySet) {
        // The server knows of an email this page does not show yet: catch up.
        linkedOnClose.current = true;
        close();
      }
    } finally {
      sendInFlight.current = false;
      setIsSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      closeOnEsc={!isSending}
      closeOnOverlayClick={!isSending}
      onCloseComplete={() => {
        onClosed?.();
        if (!linkedOnClose.current) return;
        linkedOnClose.current = false;
        onLinked();
      }}
      initialFocusRef={inputRef}
      isCentered
    >
      <ModalOverlay />
      <ModalContent as="form" noValidate onSubmit={submit} bg="bg.overlay" color="text.primary" mx={4}>
        <ModalHeader>{intl.formatMessage({ id: "signInMethods.email.title" })}</ModalHeader>
        <ModalCloseButton
          isDisabled={isSending}
          boxSize={TOUCH_TARGET}
          _focusVisible={FOCUS_RING}
        />
        <ModalBody>
          <Text mb={4} color="text.secondary">
            {intl.formatMessage({ id: "signInMethods.email.body" })}
          </Text>
          <FormControl isInvalid={!!errorId}>
            <FormLabel htmlFor="link-email-address" fontSize="sm" fontWeight="medium">
              {intl.formatMessage({ id: "signInMethods.email.addressLabel" })}
            </FormLabel>
            <Input
              id="link-email-address"
              ref={inputRef}
              type="email"
              autoComplete="email"
              value={address}
              onChange={(event) => {
                setAddress(event.target.value);
                setErrorId(null);
              }}
              onBlur={() => setErrorId(address ? emailAddressErrorId(address) : null)}
            />
            {errorId && <FormErrorMessage>{intl.formatMessage({ id: errorId })}</FormErrorMessage>}
          </FormControl>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button
            variant="outline"
            borderColor="border.strong"
            minH={TOUCH_TARGET}
            isDisabled={isSending}
            onClick={close}
            _focusVisible={FOCUS_RING}
          >
            {intl.formatMessage({ id: "signInMethods.email.cancel" })}
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            minH={TOUCH_TARGET}
            isLoading={isSending}
            _focusVisible={FOCUS_RING}
          >
            {intl.formatMessage({ id: "signInMethods.email.submit" })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EmailLinkModal;
